import Button from 'components/buttons/Button';
import { BodyRegular, Headline3, Headline4 } from 'components/Elements';
import { ErrorNotifier } from 'components/notifiers/ErrorNotifier';
import { EmptyTableContent } from 'components/table/EmptyTableContent';
import Table, { TableHeader, TableRow } from 'components/table/Table';
import { AuthContext } from 'contextProviders/AuthContext';
import { OrganisationOverviewContext } from 'contextProviders/OrganisationOverviewContext';
import useGetRequest from 'hooks/useGetRequest';
import {
    OrganisationBilling,
    MonthlyBill,
    CourseBill,
    EnrollmentBill,
} from 'models/OrganisationBilling';
import PaymentSetupModal from 'pages/OA/components/PaymentSetupModal';
import React from 'react';
import { styled } from 'styled-components';
import { breakpoints, sizes, theme } from 'theme';
import strings from '../../../strings/strings.json';
import { BillCard } from './BillCard';

type BillingListBaseProps = {
    children?: React.ReactNode;
};

function BillingListBase({ children }: BillingListBaseProps) {
    const authContext = React.useContext(AuthContext);
    const [openModal, setOpenModal] = React.useState<boolean>(false);

    function closeModal() {
        setOpenModal(false);
    }

    async function closeModalAfterAddCard() {
        await authContext.refreshUserData();
    }

    return (
        <BillingListContainer>
            {openModal && (
                <PaymentSetupModal closeModal={closeModal} closeModalAfterAddCard={closeModalAfterAddCard} />
            )}
            <HeadlineAndButton>
                <Headline3>{strings.billing.title}</Headline3>
                <Button
                    title={strings.billing.setupPaymentCardButton.title}
                    aria={strings.billing.setupPaymentCardButton.aria}
                    onClick={() => setOpenModal(true)}
                />
            </HeadlineAndButton>
            {children}
        </BillingListContainer>
    );
}

const HeadlineAndButton = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export default function AdminBillingList(): JSX.Element {
    const { data: organisationBills, loading, errors } = useGetRequest<OrganisationBilling[]>('billing/all');

    if (loading) return <BillingListBase />;

    if (errors.length)
        return (
            <BillingListBase>
                <ErrorNotifier title={strings.billing.errorTitle} description={strings.billing.errorDescription} />
            </BillingListBase>
        );

    return (
        <BillingListBase>
            {organisationBills &&
                organisationBills.map((organisationBill, index) => {
                    return (
                        <OrganisationCard key={index}>
                            <Headline4>{organisationBill.organisationName}</Headline4>

                            {organisationBill.monthlyBills.map((monthlyBill, monthlyBillIndex) => {
                                return <BillCard key={monthlyBillIndex} monthlyBill={monthlyBill} />;
                            })}
                        </OrganisationCard>
                    );
                })}
        </BillingListBase>
    );
}

export function OrganisationBill(): JSX.Element {
    const { payments } = React.useContext(OrganisationOverviewContext);

    const headers: TableHeader[] = [
        { name: 'Date', rowPercentage: 25 },
        { name: 'Amount', rowPercentage: 15 },
        { name: 'Description', rowPercentage: 60 },
    ];

    const rows: TableRow[] = payments.map((p, i) => {
        return {
            id: i.toString(),
            cells: [
                {
                    name: 'Date',
                    value: new Date(p.date).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                    }),
                },
                { name: 'Amount', value: p.amount },
                { name: 'Description', value: p.description },
            ],
        };
    });

    return (
        <BillingListBase>
            <Table
                headers={headers}
                rows={rows}
                emptyTableContent={<EmptyTableContent title="No payments." description="" />}
            />
        </BillingListBase>
    );
}

export type BillCardProps = {
    monthlyBill: MonthlyBill;
};

export const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

export const MainRow = styled.div`
    display: grid;
    width: 75%;
    grid-template-columns: repeat(5, 1fr);

    @media (max-width: ${breakpoints.lg}) {
        width: 100%;
    }

    @media (max-width: ${breakpoints.md}) {
        grid-template-columns: repeat(3, 1fr);
        row-gap: ${sizes.spacingSm};
    }

    @media (max-width: ${breakpoints.sm}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: ${breakpoints.xs}) {
        grid-template-columns: 1fr;
    }
`;

export const Cell = styled.div`
    display: flex;
    flex-direction: column;
`;

const BillingListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingRg};
`;

const OrganisationCard = styled.div`
    display: flex;
    flex-direction: column;
`;

type CourseListProps = {
    courseBills: CourseBill[];
    enrollmentBills: EnrollmentBill[];
};

export function CourseList({ courseBills, enrollmentBills }: CourseListProps): JSX.Element {
    let itemNumber = 1;

    return (
        <CourseListContainer>
            {courseBills.map((courseBill, index) => (
                <CourseItem key={index}>
                    <CourseTitleRow>
                        <BodyRegular $bold>{`${itemNumber++}. ${courseBill.courseTitle}`}</BodyRegular>
                    </CourseTitleRow>
                    <BreakdownRow>
                        <BreakdownColumn>
                            <BodyRegular $bold>{strings.billing.fields.dateCreated}</BodyRegular>
                            <BodyRegular>{`${new Date(courseBill.courseCreateDate).toLocaleDateString()}`}</BodyRegular>
                        </BreakdownColumn>
                        <BreakdownColumn>
                            <BodyRegular $bold>{strings.billing.fields.courseFee}</BodyRegular>
                            <BodyRegular>{`£${courseBill.courseFee}`}</BodyRegular>
                        </BreakdownColumn>
                    </BreakdownRow>
                </CourseItem>
            ))}
            {enrollmentBills.map((enrollmentBill, index) => (
                <CourseItem key={index}>
                    <CourseTitleRow>
                        <BodyRegular $bold>{`${itemNumber++}. ${enrollmentBill.courseTitle}`}</BodyRegular>
                    </CourseTitleRow>
                    <BreakdownRow>
                        <BreakdownColumn>
                            <BodyRegular $bold>{strings.billing.fields.dateCreated}</BodyRegular>
                            <BodyRegular>{`${new Date(enrollmentBill.courseCreateDate).toLocaleDateString()}`}</BodyRegular>
                        </BreakdownColumn>
                        <BreakdownColumn>
                            <BodyRegular $bold>{strings.billing.fields.enrollmentsFee}</BodyRegular>
                            <BodyRegular>{`£${enrollmentBill.enrollmentsFee} (+${enrollmentBill.numberOfEnrollments} ${strings.billing.fields.learner})`}</BodyRegular>
                        </BreakdownColumn>
                    </BreakdownRow>
                </CourseItem>
            ))}
        </CourseListContainer>
    );
}

const CourseTitleRow = styled.div`
    display: flex;
    gap: ${sizes.spacingSm};
    width: 100%;
`;

const BreakdownColumn = styled.div`
    display: flex;
    flex-direction: column;
`;

const BreakdownRow = styled.div`
    display: flex;
    gap: ${sizes.spacingXl};

    @media (max-width: ${breakpoints.xs}) {
        flex-direction: column;
        gap: ${sizes.spacingXs};
    }
`;

const CourseItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingSm};
    border-top: 1px solid ${theme.cardBorder};
    padding-top: ${sizes.spacingMd};
`;

const CourseListContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;
